import type { RouteComponentProps } from '@gatsbyjs/reach-router';
import { useLocation } from '@gatsbyjs/reach-router';
import type { FieldProps, FormikHelpers } from 'formik';
import { ErrorMessage, Field, Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React from 'react';
import { defineMessages, useIntl } from 'react-intl';
import { useSearchParam } from 'react-use';
import * as Yup from 'yup';
import type { Schema } from 'yup';

import { FormikCodeField } from '@components/FormikFields';
import { PasswordRules } from '@components/PasswordRules';
import RoundedButton from '@components/RoundedButton/index';
import TextInputBox from '@components/TextInputBox';

import * as ROUTES from '@constants/routes';

import useCommonSchemas from '@hooks/schemas/useCommonSchemas';

import AuthLayout, { HorizontalRule, Subtitle } from '@layouts/AuthLayout';

import { useConfirmPasswordMutation } from 'generated/session.generated';

const messages = defineMessages({
  newPassword: {
    id: 'passwordReset.token.newPassword',
    defaultMessage: 'Reset password',
  },
  password: {
    id: 'passwordReset.token.password',
    defaultMessage: 'New password',
  },
  email: {
    id: 'GLOBAL.email',
    defaultMessage: 'Email',
  },
  reset: {
    id: 'passwordReset.token.reset',
    defaultMessage: 'Reset',
  },
  repeatedPassword: {
    id: 'passwordReset.token.repeatedPassword',
    defaultMessage: 'Confirm new password',
  },
  sendAnother: {
    id: 'passwordReset.token.sendAnother',
    defaultMessage: 'Send Another',
  },
  tokenError: {
    id: 'passwordReset.token.tokenError',
    defaultMessage: 'Reset Error',
  },
  passwordMismatch: {
    id: 'passwordReset.token.passwordMismatch',
    defaultMessage: 'Passwords do not match',
  },
  errorResetting: {
    id: 'passwordReset.token.errorResetting',
    defaultMessage: 'There was an error resetting your password',
  },
  requiredText: {
    id: 'GLOBAL.requiredText',
    defaultMessage: 'Required',
  },
  subheader: {
    id: 'passwordReset.token.subheader',
    defaultMessage:
      'Enter the code below, along with your email and new password.',
  },
  passwordsMustMatch: {
    id: 'passwordRules.passwordsMustMatch',
    defaultMessage: 'Passwords must match',
  },
});

type FormValues = {
  code: string[];
  email: string;
  password: string;
  repeatedPassword: string;
  submit: string | null;
};

const ResetForm: React.FC<RouteComponentProps> = () => {
  const { formatMessage } = useIntl();
  const location = useLocation();
  const deliveryMessage = (
    location.state as { deliveryMessage?: string } | undefined
  )?.deliveryMessage;

  const [resetPassword] = useConfirmPasswordMutation();
  const { emailSchema, passwordSchema } = useCommonSchemas();

  const email = useSearchParam('email');

  const initialValues: FormValues = {
    code: ['', '', '', '', '', ''],
    email: typeof email === 'string' ? email : '',
    password: '',
    repeatedPassword: '',
    submit: null,
  };

  const ResetFormSchema: Schema<Partial<FormValues>> = Yup.object().shape({
    code: Yup.array().of(
      Yup.string().required(formatMessage(messages.requiredText))
    ),
    email: emailSchema.required(formatMessage(messages.requiredText)),
    repeatedPassword: Yup.string()
      .oneOf([Yup.ref('password')], formatMessage(messages.passwordsMustMatch))
      .required(formatMessage(messages.requiredText)),
    password: passwordSchema.required(formatMessage(messages.requiredText)),
  });

  async function handleSubmit(
    { email, password, repeatedPassword, code }: FormValues,
    { setErrors }: FormikHelpers<FormValues>
  ) {
    try {
      // Repeat does not match
      if (password !== repeatedPassword) {
        setErrors({
          repeatedPassword: formatMessage(messages.passwordMismatch),
        });
        return;
      }

      const { errors } = await resetPassword({
        variables: {
          input: {
            email,
            newPassword: password,
            verificationCode: code.join(''),
          },
        },
      });

      if (errors?.length) {
        setErrors({
          submit:
            errors.map(({ message }) => message).join(', ') ||
            formatMessage(messages.errorResetting),
        });
        return;
      }

      navigate(ROUTES.FORGOT_PASSWORD_SUCCESS);
    } catch (error) {
      setErrors({ submit: formatMessage(messages.errorResetting) });
    }
  }

  return (
    <AuthLayout title={formatMessage(messages.newPassword)}>
      {deliveryMessage && <Subtitle>{deliveryMessage}</Subtitle>}
      <Subtitle>{formatMessage(messages.subheader)}</Subtitle>
      <Formik
        initialValues={initialValues}
        onSubmit={handleSubmit}
        validationSchema={ResetFormSchema}
      >
        {({ isSubmitting, values }) => (
          <Form>
            <Field name="code">
              {({ field }: FieldProps<string>) => (
                <FormikCodeField
                  {...field}
                  autoSubmit={false}
                  codeLength={values.code.length}
                />
              )}
            </Field>
            <Field name="email">
              {({ field, meta }: FieldProps<string>) => (
                <TextInputBox
                  autoComplete="off"
                  hasError={Boolean(meta.touched && meta.error)}
                  label={formatMessage(messages.email)}
                  helperText={meta.touched ? meta.error : undefined}
                  placeholder="user@example.com"
                  required
                  type="email"
                  disabled={true}
                  {...field}
                />
              )}
            </Field>

            <Field name="password">
              {({ field, meta }: FieldProps<string>) => (
                <TextInputBox
                  autoComplete="new-password"
                  hasError={Boolean(meta.touched && meta.error)}
                  label={formatMessage(messages.password)}
                  helperText={meta.touched ? meta.error : undefined}
                  placeholder="••••••••••"
                  required
                  type="password"
                  {...field}
                />
              )}
            </Field>

            <Field name="repeatedPassword">
              {({ field, meta }: FieldProps<string>) => (
                <TextInputBox
                  autoComplete="off"
                  hasError={Boolean(meta.touched && meta.error)}
                  label={formatMessage(messages.repeatedPassword)}
                  helperText={meta.touched ? meta.error : undefined}
                  placeholder="••••••••••"
                  required
                  type="password"
                  {...field}
                />
              )}
            </Field>

            <PasswordRules />

            <HorizontalRule />
            <ErrorMessage
              name="submit"
              render={(e) => {
                if (typeof e !== 'string') {
                  return null;
                }
                return <p className="error-msg">{e}</p>;
              }}
            />

            <RoundedButton disabled={isSubmitting} type="submit">
              {formatMessage(messages.reset)}
            </RoundedButton>
          </Form>
        )}
      </Formik>
    </AuthLayout>
  );
};

export default ResetForm;
